import classes from "./ReferencesBlock.module.css";
import {
    ActionIcon,
    alpha,
    Box,
    Card,
    Collapse,
    Divider,
    Group,
    List,
    ThemeIcon,
    Title,
    Tooltip,
    Transition,
} from "@mantine/core";
import IconBooks from "../../../../../assets/books.svg?react";
import IconTools from "../../../../../assets/tools.svg?react";
import {SourceInfo, RelatedSourceInfo} from "../../../../../types";
import {useTranslation} from "react-i18next";
import IconBlockquote from "../../../../../assets/blockquote.svg?react";
import {FeatureGate} from "../../../../../featureFlags/FeatureGate";
import {Feature} from "../../../../../featureFlags/featureFlags";
import {useDisclosure} from "@mantine/hooks";
import {ReferenceItem} from "../../../sliceChatComponents/ReferenceItem";

// Re-export for backwards compatibility
export {ReferenceItem};

/*
type ReferencesBlockProps = {
    items: ReferenceItem[];
}; */
export const ReferencesBlock = ({
    id,
    items,
    consideredSources,
    relatedTools,
    messageContent,
    isFeedback,
}: {
    id?: number;
    items: SourceInfo[];
    consideredSources: SourceInfo[];
    relatedTools: RelatedSourceInfo[];
    messageContent?: string;
    isFeedback?: boolean;
}) => {
    const {t} = useTranslation();
    const mounted = items.length > 0;
    const [consideredOpen, {toggle}] = useDisclosure(false);

    // Filter relatedTools to only include those referenced in the message
    const filteredRelatedTools = messageContent
        ? relatedTools.filter((tool: RelatedSourceInfo) =>
              messageContent.includes(tool.url)
          )
        : relatedTools;
    const unfilteredRelatedTools = relatedTools.filter(
        (tool: RelatedSourceInfo) => !messageContent.includes(tool.url)
    );
    // Make sure all items are unique by id
    const uniqueIds = Array.from(
        new Set(filteredRelatedTools.map((item) => item.id))
    );

    return (
        <Transition transition="fade-up" duration={500} mounted={mounted}>
            {(styles) => (
                <Card
                    bg={"var(--dib-surface)"}
                    className={classes.card}
                    style={styles}
                >
                    <Box>
                        <Group justify="space-between" align="center">
                            <Group
                                gap="xs"
                                align="flex-start"
                                wrap="nowrap"
                                className={classes.cardHeader}
                            >
                                <ThemeIcon
                                    color="var(--dib-darkest-of-greens)"
                                    variant="subtle"
                                    w="var(--book-icon-size)"
                                    h="var(--book-icon-size)"
                                >
                                    <IconBooks />
                                </ThemeIcon>
                                <Title order={3} className={classes.cardTitle}>
                                    {t("references.title")}
                                </Title>
                            </Group>
                            <FeatureGate feature={Feature.InternalFeatures}>
                                <Tooltip
                                    label={t(
                                        "references.consideredSources.tooltip"
                                    )}
                                >
                                    <ActionIcon
                                        className={
                                            classes.consideredSourcesButton
                                        }
                                        variant="subtle"
                                        radius={"xl"}
                                        size="sm"
                                        onClick={toggle}
                                    >
                                        <IconBlockquote />
                                    </ActionIcon>
                                </Tooltip>
                            </FeatureGate>
                        </Group>
                        <List
                            className={classes.ol}
                            ff="text"
                            listStyleType="none"
                            spacing={"sm"}
                            styles={{
                                itemWrapper: {
                                    display: "flex",
                                    width: "100%",
                                    alignItems: "stretch",
                                },
                            }}
                        >
                            {items.map((source: SourceInfo) => {
                                return (
                                    <ReferenceItem
                                        index={source.index + 1}
                                        key={source.url}
                                        id={source.id}
                                        desc={source.description}
                                        type={source.type}
                                        href={source.url}
                                        content={
                                            source.segmentName
                                                ? `${source.name} | ${source.segmentName}`
                                                : source.name
                                        }
                                    />
                                );
                            })}
                        </List>
                        <Collapse in={consideredOpen}>
                            <Divider
                                color={alpha("var(--dib-dark-green)", 0.2)}
                            />
                            <Group
                                gap="xs"
                                align="flex-start"
                                wrap="nowrap"
                                className={classes.cardHeader}
                            >
                                <Title
                                    style={{
                                        paddingLeft:
                                            "calc(var(--book-icon-size) + var(--mantine-spacing-md))",
                                    }}
                                    order={4}
                                    fz={"md"}
                                    className={classes.cardTitle}
                                >
                                    {t("references.consideredSources.title")}
                                </Title>
                            </Group>
                            <List
                                className={classes.ol}
                                ff="text"
                                listStyleType="none"
                                spacing={"sm"}
                                styles={{
                                    itemWrapper: {
                                        display: "flex",
                                        width: "100%",
                                        alignItems: "stretch",
                                    },
                                }}
                            >
                                {consideredSources.map((source: SourceInfo) => {
                                    return (
                                        <ReferenceItem
                                            index={source.index + 1}
                                            key={source.id}
                                            id={source.id}
                                            desc={source.description}
                                            type={source.type}
                                            href={source.url}
                                            content={
                                                source.segmentName
                                                    ? `${source.name} | ${source.segmentName}`
                                                    : source.name
                                            }
                                        />
                                    );
                                })}
                            </List>
                        </Collapse>
                    </Box>
                </Card>
            )}
        </Transition>
    );
};
